import { useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/store/app-store'
import type { User } from '@supabase/supabase-js'

export function useAuth() {
  const { user, loading, setUser, setLoading, logout } = useAuthStore()
  const queryClient = useQueryClient()

  // Listen for auth state changes
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'SIGNED_IN' && session?.user) {
          setUser({
            id: session.user.id,
            email: session.user.email!,
            name: session.user.user_metadata?.name || session.user.email!,
            role: session.user.user_metadata?.role || 'user',
            avatar: session.user.user_metadata?.avatar_url,
            franchise_ids: session.user.user_metadata?.franchise_ids || []
          })
        } else if (event === 'SIGNED_OUT') {
          logout()
          queryClient.clear()
        }
        setLoading(false)
      }
    )

    return () => subscription.unsubscribe()
  }, [setUser, setLoading, logout, queryClient])

  // Get initial session
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        setUser({
          id: session.user.id,
          email: session.user.email!,
          name: session.user.user_metadata?.name || session.user.email!,
          role: session.user.user_metadata?.role || 'user',
          avatar: session.user.user_metadata?.avatar_url,
          franchise_ids: session.user.user_metadata?.franchise_ids || []
        })
      }
      setLoading(false)
    })
  }, [setUser, setLoading])

  const signInMutation = useMutation({
    mutationFn: async ({ email, password }: { email: string; password: string }) => {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      })
      if (error) throw error
      return data
    }
  })

  const signOutMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.auth.signOut()
      if (error) throw error
    },
    onSuccess: () => {
      logout()
      queryClient.clear()
    }
  })

  return {
    user,
    loading,
    isAuthenticated: !!user,
    signIn: signInMutation.mutate,
    signOut: signOutMutation.mutate,
    isSigningIn: signInMutation.isPending,
    isSigningOut: signOutMutation.isPending
  }
}